import { ChevronLeftIcon, ChevronRightIcon, emptyFragment } from '@execonline-inc/execonline-ui';
import { fromNullable } from 'maybeasy';
import { observer } from 'mobx-react';
import { dashboardUrl } from '../../../ClientRoutes';
import { nav } from '../../../Navigation';
import ProgramStore from '../../../ProgramStore';
import { whenActive } from '../../../ProgramStore/Types';
import SegmentStore from '../../../SegmentStore';
import { T } from '../../../Translations';
import { useTranslationsContext } from '../../../Translations/UseTranslationsContext';
import { AdvanceButton } from './Common';
import NextAdvanceButton from './NextAdvanceButton';
import PreviousAdvanceButton from './PreviousAdvanceButton';

interface Props {
  store: SegmentStore;
  programStore: ProgramStore;
}

// The learner is on the last segment of the program, so the program is done from here
// on. Nothing follows it, so the page offers a way back to the dashboard.
const isLastProgramSegment = (store: SegmentStore, programStore: ProgramStore): boolean =>
  store.segmentResource
    .map((resource) => resource.payload.id)
    .andThen((segmentId) =>
      programStore.programDetailResource
        .andThen((resource) => whenActive(resource.payload))
        .andThen((program) => fromNullable(program.modules.flatMap((m) => m.segments).at(-1)))
        .map((lastSegment) => lastSegment.id === segmentId),
    )
    .getOrElseValue(false);

function SegmentNavigation({ store, programStore }: Props) {
  const { translate } = useTranslationsContext();
  const lastSegment = isLastProgramSegment(store, programStore);

  return programStore.progress
    .map((progress) => {
      switch (progress.kind) {
        case 'program-progress':
        case 'module-progress':
          return (
            <div className="flex flex-1 flex-wrap justify-between gap-x-4 gap-y-2">
              <PreviousAdvanceButton
                data-testid="previous-segment-button"
                store={store}
                startContent={<ChevronLeftIcon color="inherit" size="sm" />}
              >
                <T kind="Previous Segment" />
              </PreviousAdvanceButton>
              {lastSegment && (
                <AdvanceButton
                  data-testid="back-to-dashboard-button"
                  data-intercom-target="segment-back-to-dashboard-button"
                  className="ml-auto"
                  title={translate('Back to Dashboard')}
                  onClick={() => {
                    nav(dashboardUrl);
                  }}
                >
                  <T kind="Back to Dashboard" />
                </AdvanceButton>
              )}
              {!store.nextUnavailable && (
                <NextAdvanceButton
                  data-testid="next-segment-button"
                  className={lastSegment ? undefined : 'ml-auto'}
                  store={store}
                  endContent={<ChevronRightIcon color="inherit" size="sm" />}
                >
                  <T kind="Next Segment" />
                </NextAdvanceButton>
              )}
            </div>
          );
        case 'registration-segment-progress':
          return (
            <div className="flex flex-1">
              <NextAdvanceButton
                data-testid="next-step-button"
                className="ml-auto"
                store={store}
                endContent={<ChevronRightIcon color="inherit" size="sm" />}
              >
                <T kind="Next Step" />
              </NextAdvanceButton>
            </div>
          );
      }
    })
    .getOrElse(emptyFragment);
}

export default observer(SegmentNavigation);
